import { useCallback } from 'react';

// Goal feedback preferences stored in localStorage
const STORAGE_KEY = 'sg7-goal-feedback';

interface GoalFeedbackSettings {
  soundEnabled: boolean;
  vibrationEnabled: boolean;
}

const defaultSettings: GoalFeedbackSettings = {
  soundEnabled: true,
  vibrationEnabled: true,
};

export function getGoalFeedbackSettings(): GoalFeedbackSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Error reading goal feedback settings:', e);
  }
  return defaultSettings;
}

export function saveGoalFeedbackSettings(settings: GoalFeedbackSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Error saving goal feedback settings:', e);
  }
}

// Shared audio element + unlock on first user interaction
const sharedAudio = new Audio('/sounds/goal-alert.mp3');
sharedAudio.preload = 'auto';
sharedAudio.volume = 1.0;

function unlockAudio() {
  sharedAudio.muted = true;
  sharedAudio.play().then(() => {
    sharedAudio.pause();
    sharedAudio.muted = false;
    sharedAudio.currentTime = 0;
  }).catch(() => {});
  document.removeEventListener('click', unlockAudio);
  document.removeEventListener('touchstart', unlockAudio);
}
document.addEventListener('click', unlockAudio, { once: true });
document.addEventListener('touchstart', unlockAudio, { once: true });

function playGoalSound() {
  const settings = getGoalFeedbackSettings();

  if (settings.soundEnabled) {
    sharedAudio.currentTime = 0;
    sharedAudio.play().catch((err) => {
      console.log('Audio play failed:', err.message);
    });
  }

  if (settings.vibrationEnabled && 'vibrate' in navigator) {
    try {
      navigator.vibrate([200, 100, 200]);
    } catch (e) {
      console.log('Vibration failed:', e);
    }
  }
}

export function useGoalFeedback() {
  const triggerGoalFeedback = useCallback(() => {
    playGoalSound();
  }, []);

  return { triggerGoalFeedback };
}

// Standalone function for use outside React components
export function triggerGoalFeedbackStandalone(): void {
  playGoalSound();
}
